// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { SegmentedControl } from '../SegmentedControl';
import styles from './ModeTabs.module.css';

export interface ModeTab {
  id: string;
  label: string;
  /** Shown instead of `label` on a narrow top bar (below 75rem); the full label stays the name and tooltip. */
  shortLabel?: string;
  /** Case-relative href, query included (the focus travels with the tab). */
  href: string;
  /** Badge text (`badgeText`), or null for none. */
  badge?: string | null;
  /** What the badge counts, for the tooltip ("4 typer i kø"). */
  badgeTitle?: string;
}

export interface ModeTabsProps {
  tabs: readonly ModeTab[];
  current: string;
}

/**
 * The five mode tabs as a segmented control of links (a mode is a route, so
 * it opens in a new tab like any link). The current one carries
 * `aria-current="page"`; a badge shows what waits there. A long label has a
 * short form for a narrow bar ("Rapp & Indb."), hidden from assistive tech,
 * which reads the full one.
 */
export function ModeTabs({ tabs, current }: ModeTabsProps) {
  return (
    <SegmentedControl
      role="nav"
      aria-label="Visning"
      scroll
      items={tabs.map((t) => ({
        value: t.id,
        label: t.shortLabel ? (
          <>
            <span className={styles.full}>{t.label}</span>
            <span className={styles.short} aria-hidden="true">
              {t.shortLabel}
            </span>
          </>
        ) : (
          t.label
        ),
        href: t.href,
        badge: t.badge,
        title: t.badge && t.badgeTitle ? `${t.label} · ${t.badgeTitle}` : t.shortLabel ? t.label : undefined,
      }))}
      value={current}
    />
  );
}
